import React, { useCallback, useEffect, useRef, useState } from "react";
import { DropdownBody, DropdownItem, Icons, useToast } from "fds";
import { DocInfo, LogItem } from "../../../types/files";
import { t } from "i18next";
import { handleDocDownload, handleDocPreview } from "../../../shared/utils/api";
import utilsCommon from "../../../shared/utils/common";
import useDocumentStore from "../../../redux/dispatcher/useDocumentStore";
import ShareLinkModal from "../../../components/More/ShareLinkModal";
import ReadRequestModal from "../../../components/More/ReadRequestModal";
import { nanoid } from "@reduxjs/toolkit";
import useLogStore from "../../../redux/dispatcher/useLogStore";
import ChangeWorkgroupFileAuthModal from "../../../components/More/ChangeWorkgroupFileAuthModal";
import ChangeFileAuthModal from "../../../components/More/ChangeFileAuthModal";
import docApi from "../../../redux/api/DocApi";
interface LogSettingButtonProps {
  settingButtonInfo: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
    docInfo?: DocInfo;
  };
  setSettingInfo: (req: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
    docInfo?: DocInfo;
  }) => void;
  page?: "fileLog" | "log";
}
const LogSettingButton: React.FC<LogSettingButtonProps> = ({
  settingButtonInfo,
  setSettingInfo,
  page,
}) => {
  const toast = useToast();
  const [modalType, changeModalType] = useState("CLOSE");

  const { getContentDownload, getContentDetail } = useDocumentStore();
  const { getLogDetail } = useLogStore();
  let menus = [
    {
      key: "preview",
      resource: utilsCommon.renderViewTooltip(
        settingButtonInfo?.log?.documnetName ?? ""
      ),
      icon: <Icons variant="line" label="preview" isOn />,
      historyType: 1,
      onClick: () => checkDocValid(1, "preview"),
    },
    {
      key: "editMode",
      resource: utilsCommon.renderEditTooltip(
        settingButtonInfo?.log?.documnetName ?? ""
      ),
      historyType: 1,
      isHidden: !window.eco.isCspp,
      icon: <Icons variant="line" label="edit" isOn />,
      onClick: () =>
        settingButtonInfo?.log &&
        gotoEditMode(
          settingButtonInfo?.log?.workgroupId,
          settingButtonInfo.log?.syncId
        ),
    },
    {
      key: "download",
      resource: t("file.action.download"),
      historyType: 1,
      icon: <Icons variant="line" label="download" />,
      onClick: () => checkDocValid(1, "download"),
    },
    {
      key: "shareLink",
      resource: t("file.action.shareLink"),
      icon: <Icons variant="line" label="link" />,
      historyType: 1,
      isHidden: page === "fileLog",
      onClick: () => checkDocValid(1, "SHARE_LINK"),
    },
    {
      key: "auth",
      resource: t("file.action.auth"),
      icon: <Icons variant="line" label="access" />,
      historyType: 1,
      onClick: () => checkDocValid(1, "CHANGE_FILE_AUTH"),
    },
    {
      key: "requestOpen",
      resource: t("file.action.sendReadRequest"),
      icon: <Icons variant="line" label="alertOctagon" />,
      historyType: 1,
      isHidden: page === "fileLog",
      onClick: () => checkDocValid(1, "READ_REQUEST"),
    },
  ];
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'
  const inputRef = useRef<HTMLTableRowElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!inputRef.current) return;
    const observer = new ResizeObserver(() => {
      if (inputRef.current) {
        setHeight(inputRef.current.offsetHeight);
      }
    });
    observer.observe(inputRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (settingButtonInfo.log) return;
    getLogDetail({ logIdList: [settingButtonInfo.logId] }).then((resp: any) =>
      setSettingInfo({ ...settingButtonInfo, log: resp.payload.logResults[0] })
    );
  }, [settingButtonInfo.logId]);
  /** 편집 버전으로 열기 */
  const gotoEditMode = async (
    workgroupId: string,
    syncId: string
  ): Promise<void> => {
    const newSyncInfo = await docApi.getDocSyncInfo({ workgroupId, syncId });

    if (
      newSyncInfo?.status === "N" &&
      newSyncInfo?.authType === "R" &&
      newSyncInfo?.checkoutUser.userId === ""
    ) {
      window.open(
        `/docs/preview?permission=edit&workgroupId=${workgroupId}&syncId=${syncId}&revisionNo=${newSyncInfo.revisionNo}&isClient=false`
      );
    } else {
      // await getSyncInfo({ workgroupId, syncId }); //TODO :  state setting

      if (newSyncInfo?.status === "T") {
        toast.toastMsg(nanoid(), t("file.alert.contentDelete"), "error");
      } else if (
        newSyncInfo?.authType === "V" ||
        newSyncInfo.authType !== "R"
      ) {
        toast.toastMsg(nanoid(), t("file.alert.checkoutFail"), "error");
      } else if (newSyncInfo?.checkoutUser.userId !== "") {
        toast.toastMsg(nanoid(), t("file.alert.checkoutAnotherUser"), "error");
      }
    }
    //권한 체크
  };
  const checkDocValid = async (historyType: number, action: string) => {
    if (!settingButtonInfo?.log) return;

    if (historyType === 1) {
      let resp: any = await getContentDetail({
        workgroupId: settingButtonInfo?.log?.workgroupId,
        syncId: settingButtonInfo.log?.syncId,
      });
      if (resp.payload) {
        if (
          !resp.payload.documentInfo ||
          resp.payload.documentInfo.status !== "N"
        ) {
          toast.toastMsg(nanoid(), t("file.alert.contentDelete"), "error");
          return;
        }
        setSettingInfo({
          ...settingButtonInfo,
          docInfo: resp.payload.documentInfo,
        });
      }
      switch (action) {
        case "preview":
          return handleDocPreview({
            permission: "view",
            workgroupId: settingButtonInfo.log?.workgroupId,
            syncId: settingButtonInfo?.log.syncId,
            toast,
            t,
            revisionNo: settingButtonInfo.log?.revisionNo,
          });
        case "download":
          return handleDocDownload(
            settingButtonInfo.log?.syncId,
            getContentDownload,
            toast,
            t
          );
        case "SHARE_LINK":
          return changeModalType("SHARE_LINK");
        case "CHANGE_FILE_AUTH":
          return changeModalType("CHANGE_FILE_AUTH");
        case "READ_REQUEST":
          return changeModalType("READ_REQUEST");
      }
    }
  };
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });
  const handleTurnOffSettng = (): void => {
    setSettingInfo({
      x: 0,
      y: 0,
      logId: "",
    });
  };
  return modalType === "CLOSE" ? (
    <DropdownBody
      maxWidth={window.eco.isCspp ? 330 : 300}
      dropdownRef={inputRef}
      minWidth={190}
      width={"fit-content"}
      location={{
        x:
          settingButtonInfo.x -
          (direction === "ltr" && settingButtonInfo.x > window.innerWidth / 2
            ? window.eco.isCspp
              ? inputRef.current?.getBoundingClientRect().width ?? 190
              : 190
            : 0) -
          (direction === "rtl" &&
          window.eco.companyName === "linkApp" &&
          !utilsCommon.isDemoEco()
            ? 38
            : 0) -
          (direction === "ltr"
            ? (document.getElementById("sidemenuId")?.getBoundingClientRect()
                .width ?? 240) - 245
            : 0) -
          (window.eco.serviceType === "OCI" ? 55 : 0),
        y:
          settingButtonInfo.y -
          (settingButtonInfo.y > window.innerHeight / 2 ? height : 0),
      }}
    >
      {menus
        .filter(
          (item) =>
            settingButtonInfo.log?.historyType === item.historyType &&
            !item.isHidden
        )
        .map((item) => (
          <DropdownItem
            height={item.key === "divider" ? undefined : 20}
            type={item.key.includes("delete") ? "warning" : "default"}
            state={item.key === "divider" ? "divider" : "default"}
            label={item.resource}
            frontIcon={item.icon}
            onClick={item.onClick}
          />
        ))}
    </DropdownBody>
  ) : (
    <>
      {modalType === "SHARE_LINK" && (
        <ShareLinkModal
          open={modalType === "SHARE_LINK"}
          onClose={handleTurnOffSettng}
          docInfo={settingButtonInfo.docInfo}
        />
      )}
      {modalType === "READ_REQUEST" && (
        <ReadRequestModal
          open={modalType === "READ_REQUEST"}
          onClose={handleTurnOffSettng}
          docInfo={settingButtonInfo.docInfo}
        />
      )}
      {modalType === "CHANGE_FILE_AUTH" &&
        (settingButtonInfo.docInfo?.workgroup.workgroupId ===
        "NONEWORKGROUP" ? (
          <ChangeFileAuthModal
            open={modalType === "CHANGE_FILE_AUTH"}
            onClose={handleTurnOffSettng}
            docInfo={settingButtonInfo.docInfo}
          />
        ) : (
          <ChangeWorkgroupFileAuthModal
            open={modalType === "CHANGE_FILE_AUTH"}
            onClose={handleTurnOffSettng}
            docInfos={
              settingButtonInfo.docInfo
                ? [settingButtonInfo.docInfo]
                : undefined
            }
          />
        ))}
    </>
  );
};
export default LogSettingButton;
